import { useState, useEffect } from "react";
import { Search, X, Box } from "lucide-react";
import { TeamIcon } from "@/components/icons/TeamIcon";
import { Button } from "@/components/ui/button";
import { ContentIcon } from "@/components/content/ContentIcon";
import { ContentType } from "@/types/content";
import { Room } from "@/types/rooms";

interface SearchItem {
  id: string;
  title: string;
  category: "content" | "rooms";
  type?: ContentType;
  isTeamRoom?: boolean;
}

interface SearchCommandProps {
  content?: Array<{ id: string; title: string; type: ContentType }>;
  rooms?: Room[];
  onItemSelect?: (item: SearchItem) => void;
  onSearchChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SearchCommand = ({
  content = [],
  rooms = [],
  onItemSelect,
  onSearchChange,
  open = true,
  onOpenChange,
}: SearchCommandProps = {}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Transform content and rooms into search items
  const contentItems: SearchItem[] = content.map((item) => ({
    id: item.id,
    title: item.title,
    category: "content" as const,
    type: item.type,
  }));

  const roomItems: SearchItem[] = rooms.map((room) => ({
    id: room.id,
    title: room.name,
    category: "rooms" as const,
    isTeamRoom: !!room.team_id,
  }));

  // Set initial selected item when dialog opens
  useEffect(() => {
    if (!open) {
      // Clear selection when dialog closes
      setSelectedItem(null);
    }
    // Don't auto-select first item - only show active on hover
  }, [open]);

  if (!open) return null;

  const filteredContent = contentItems.filter((item) => item.title.toLowerCase().includes(searchValue.toLowerCase()));

  const filteredRooms = roomItems.filter((item) => item.title.toLowerCase().includes(searchValue.toLowerCase()));

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const handleItemClick = (item: SearchItem) => {
    setSelectedItem(item.id);
    onItemSelect?.(item);

    // Navigate to the content or room
    if (item.category === "content") {
      const href = item.type === "chat" ? `/chat/${item.id}` : `/content/${item.id}`;
      window.location.href = href;
    } else if (item.category === "rooms") {
      window.location.href = `/room/${item.id}`;
    }

    // Close the dialog
    onOpenChange?.(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50" onClick={() => onOpenChange?.(false)} />

      {/* Dialog */}
      <div
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border bg-popover p-0 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-5 w-5 shrink-0 opacity-50" />
          <input
            type="text"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg opacity-70 hover:opacity-100"
            onClick={() => onOpenChange?.(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Command List */}
        <div className="max-h-[380px] overflow-y-auto overflow-x-hidden p-1">
          {/* No results message */}
          {filteredContent.length === 0 && filteredRooms.length === 0 && searchValue && (
            <div className="py-6 text-center text-sm text-muted-foreground">No results found for "{searchValue}"</div>
          )}

          {/* Content Group */}
          {filteredContent.length > 0 && (
            <div className="overflow-hidden p-1 text-foreground">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Content</div>
              <div>
                {filteredContent.map((item) => {
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-sm px-2 py-3 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                      role="option"
                      aria-selected={selectedItem === item.id}
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        {item.type && <ContentIcon type={item.type} className="h-5 w-5" />}
                        <span className="truncate">{item.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rooms Group */}
          {filteredRooms.length > 0 && (
            <div className="overflow-hidden p-1 text-foreground">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Rooms</div>
              <div>
                {filteredRooms.map((item) => {
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-3 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                      role="option"
                      aria-selected={selectedItem === item.id}
                    >
                      {item.isTeamRoom ? <TeamIcon className="h-5 w-5 flex-shrink-0" size={20} /> : <Box className="h-5 w-5 flex-shrink-0" />}
                      <span className="truncate">{item.title}</span>
                      {item.isTeamRoom && <span className="shrink-0 rounded border border-[hsl(var(--selected-border)/0.4)] bg-[hsl(var(--selected-subtle))] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-[hsl(var(--selected))]">Team</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state when no content or rooms */}
          {filteredContent.length === 0 && filteredRooms.length === 0 && !searchValue && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No content or rooms yet. Start by adding some content!
            </div>
          )}
        </div>
      </div>
    </>
  );
};
